/**
 * IBD 360 viewer — theme initialization.
 *
 * - This module is imported only when zoom opens or View 360 is clicked
 *   (`snippets/ibd-scripts.liquid`). A no-click Lighthouse run never downloads it.
 * - The heavy `@theme/ibd-360` module is imported only when a viewer is about
 *   to be seen. Viewers inside a closed `<dialog>` wait for `dialog:open`
 *   on the host (`zoom-dialog` / `dialog-component`) so Cloudinary frames stay
 *   off the product-page critical path.
 * - Calls `attachShopifyTheme(el)` on every `<ibd-360>` to wire variant-update,
 *   slideshow, dialog suspend/resume, and theme-editor lifecycle hooks.
 * - Calls `attachGA4(el)` (when analytics is enabled in theme settings) to
 *   forward the curated analytics event subset (`impression`,
 *   `first-interaction`, `interaction-end`, `frame-settle`, `loaded`,
 *   `hd-frame-loaded`, `error`, `ready`) to `window.dataLayer`, where GTM
 *   (loaded via Elevar) picks them up.
 *   Per-event theme toggles narrow that set via `attachGA4`'s `filter` option.
 *   When zoom tracking is also enabled, forwards `ibd360_zoom_change` too (not
 *   part of the curated set). See `.ibd/references/ibd-360-analytics.md` for
 *   the GTM-side trigger/tag wiring and custom dimension/metric registration.
 * - After attach, listeners on the dialog host call `el.preload('all')` so a
 *   `loading="manual"` viewer boots when the dialog opens (the adapter's
 *   resume() is a no-op on a never-initialized viewer).
 * - Tracks attached elements in a WeakSet so the MutationObserver below is safe
 *   to call attach() repeatedly when Horizon morphs the DOM during variant updates.
 * - Releases a viewer when it leaves the DOM: `<media-gallery>` replaces itself
 *   (and the zoom-dialog viewer inside it) on every variant change, and the
 *   adapter's `document` / `window` listeners would otherwise keep each replaced
 *   viewer alive. Release runs the adapter's and `attachGA4`'s detachers and
 *   removes the listeners this file added.
 */

/** Must match `DialogOpenEvent.eventName` in `@theme/dialog` (does not bubble). */
const DIALOG_OPEN_EVENT = 'dialog:open';

const attached = /** @type {WeakSet<Element>} */ (new WeakSet());

/** Viewers waiting on `dialog:open` before the heavy module is imported. */
const pending = /** @type {WeakSet<Element>} */ (new WeakSet());

/** Aborted when a viewer leaves the DOM: removes every listener attach() added and runs the detachers. */
const lifetimes = /** @type {WeakMap<Element, AbortController>} */ (new WeakMap());

/** @type {Promise<typeof import('@theme/ibd-360')> | null} Memoized lazy load of the viewer module. */
let ibd360ModulePromise = null;

/**
 * Host that dispatches `DialogOpenEvent` (`zoom-dialog` dispatches on itself).
 * @param {Element} el
 * @returns {Element | null}
 */
function findDialogHost(el) {
  return el.closest('zoom-dialog, dialog-component');
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isInsideClosedDialog(el) {
  const nativeDialog = el.closest('dialog');
  return nativeDialog instanceof HTMLDialogElement && !nativeDialog.open;
}

/**
 * @param {Element} el
 * @returns {AbortSignal}
 */
function lifetimeOf(el) {
  let controller = lifetimes.get(el);
  if (!controller) {
    controller = new AbortController();
    lifetimes.set(el, controller);
  }
  return controller.signal;
}

/**
 * Detach a viewer that left the DOM. A viewer that comes back (a morph that
 * moves it) is attached again by the MutationObserver.
 * @param {Element} el
 */
function release(el) {
  lifetimes.get(el)?.abort();
  lifetimes.delete(el);
  attached.delete(el);
  pending.delete(el);
}

/**
 * Loads `@theme/ibd-360` on first demand. Defining the custom element upgrades
 * any `<ibd-360>` elements already in the DOM.
 * @returns {Promise<typeof import('@theme/ibd-360')>}
 */
function ensureIbd360Module() {
  ibd360ModulePromise ??= import('@theme/ibd-360');
  return ibd360ModulePromise;
}

// Theme-settings-driven analytics config (mirrored via window.Ibd in ibd-scripts.liquid).
// Default to enabled when the config is absent to preserve prior behavior.
const ibd360Config = window.Ibd?.ibd360;
const analyticsEnabled = ibd360Config ? ibd360Config.analytics : true;
const zoomTrackingEnabled = analyticsEnabled && ibd360Config?.zoomTracking === true;

// Map each per-event config flag to its DOM event name (what attachGA4's filter receives).
const EVENT_NAME_BY_KEY = /** @type {const} */ ({
  ready: 'ibd360:ready',
  loaded: 'ibd360:loaded',
  impression: 'ibd360:impression',
  firstInteraction: 'ibd360:first-interaction',
  interactionEnd: 'ibd360:interaction-end',
  frameSettle: 'ibd360:frame-settle',
  error: 'ibd360:error',
  hdFrameLoaded: 'ibd360:hd-frame-loaded',
});

// Build the set of disabled curated events from config (an event fires unless explicitly false).
const disabledEvents = new Set();
const eventConfig = ibd360Config?.events;
if (eventConfig) {
  for (const key of /** @type {(keyof typeof EVENT_NAME_BY_KEY)[]} */ (Object.keys(EVENT_NAME_BY_KEY))) {
    if (eventConfig[key] === false) disabledEvents.add(EVENT_NAME_BY_KEY[key]);
  }
}

/** @type {(type: string) => boolean} */
const eventFilter = (type) => !disabledEvents.has(type);

/**
 * Forward `ibd360:zoom-change` to the dataLayer. It's not part of the curated
 * `attachGA4` set, so it's pushed manually, with keys flattened the way
 * `attachGA4` flattens them (`zoomLevel` → `ibd360_zoom_level`).
 * @param {Element} el
 * @param {AbortSignal} signal
 */
function trackZoom(el, signal) {
  el.addEventListener(
    'ibd360:zoom-change',
    (event) => {
      const detail = /** @type {{ zoomLevel?: number; zoomTrigger?: string }} */ (
        /** @type {CustomEvent} */ (event).detail ?? {}
      );
      (window.dataLayer ||= []).push({
        event: 'ibd360_zoom_change',
        ibd360_zoom_level:
          typeof detail.zoomLevel === 'number' ? Math.round(detail.zoomLevel * 1000) / 1000 : undefined,
        ibd360_zoom_trigger: detail.zoomTrigger,
      });
    },
    { signal }
  );
}

/**
 * @param {Element} el
 */
async function attach(el) {
  if (attached.has(el)) return;
  attached.add(el);
  const signal = lifetimeOf(el);

  const { attachGA4, attachShopifyTheme } = await ensureIbd360Module();
  // Removed while the module loaded: attaching now would leak the adapter's listeners.
  if (signal.aborted) return;

  // Detach everything on release, including listeners on `el` itself, so a
  // viewer that is re-inserted and attached again doesn't report twice.
  const detachers = [attachShopifyTheme(/** @type {any} */ (el))];
  if (analyticsEnabled) detachers.push(attachGA4(/** @type {any} */ (el), { filter: eventFilter }));
  if (zoomTrackingEnabled) trackZoom(el, signal);
  signal.addEventListener(
    'abort',
    () => {
      for (const detach of detachers) detach();
    },
    { once: true }
  );

  const host = findDialogHost(el);
  if (!host) return;

  host.addEventListener(
    DIALOG_OPEN_EVENT,
    () => {
      /** @type {any} */ (el).preload('all');
    },
    { signal }
  );
}

/**
 * Import, attach, and start frame loading once the viewer is about to be seen.
 * @param {Element} el
 */
async function attachAndPreload(el) {
  await attach(el);
  if (!el.isConnected) return;
  /** @type {any} */ (el).preload?.('all');
}

/**
 * Attach immediately, or wait for the closed zoom dialog to open so the heavy
 * module and Cloudinary HD frames stay off first paint / LCP.
 * @param {Element} el
 */
function scheduleAttach(el) {
  if (attached.has(el) || pending.has(el)) return;

  if (isInsideClosedDialog(el)) {
    const host = findDialogHost(el);
    if (!host) {
      void attach(el);
      return;
    }

    pending.add(el);
    host.addEventListener(
      DIALOG_OPEN_EVENT,
      () => {
        void attachAndPreload(el);
      },
      { once: true, signal: lifetimeOf(el) }
    );
    return;
  }

  void attachAndPreload(el);
}

/**
 * @param {ParentNode} [root]
 */
function scan(root = document) {
  for (const el of root.querySelectorAll('ibd-360')) {
    scheduleAttach(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
} else {
  scan();
}

new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.removedNodes) {
      if (!(node instanceof Element)) continue;
      const removed = node.tagName === 'IBD-360' ? [node] : node.querySelectorAll('ibd-360');
      for (const el of removed) {
        // Still connected: moved within this batch, not removed.
        if (!el.isConnected && lifetimes.has(el)) release(el);
      }
    }

    for (const node of m.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.tagName === 'IBD-360') scheduleAttach(node);
      else scan(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
